import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["field"]

  connect() {
    this.maxInitializeAttempts = 80
    this.initializeAttempts = 0
    this.initializeRetryTimer = null
    this.reinitializeDelayTimer = null
    this.boundTurboLoadInit = () => this.reinitializeAfterTurboRender()
    this.boundTurboRenderInit = () => this.reinitializeAfterTurboRender()
    this.boundTurboFrameLoadInit = () => this.reinitializeAfterTurboRender()

    document.addEventListener("turbo:load", this.boundTurboLoadInit)
    document.addEventListener("turbo:render", this.boundTurboRenderInit)
    document.addEventListener("turbo:frame-load", this.boundTurboFrameLoadInit)

    this.initializeWithRetry()
  }

  disconnect() {
    this.clearInitializeRetryTimer()
    this.clearReinitializeDelayTimer()
    if (this.boundTurboLoadInit) {
      document.removeEventListener("turbo:load", this.boundTurboLoadInit)
      this.boundTurboLoadInit = null
    }
    if (this.boundTurboRenderInit) {
      document.removeEventListener("turbo:render", this.boundTurboRenderInit)
      this.boundTurboRenderInit = null
    }
    if (this.boundTurboFrameLoadInit) {
      document.removeEventListener("turbo:frame-load", this.boundTurboFrameLoadInit)
      this.boundTurboFrameLoadInit = null
    }
    this.destroySelect2()
  }

  selectAll(event) {
    event.preventDefault()
    if (!this.hasFieldTarget) return

    Array.from(this.fieldTarget.options).forEach((option) => {
      if (option.disabled) return
      option.selected = true
    })

    this.triggerChange()
  }

  clearAll(event) {
    event.preventDefault()
    if (!this.hasFieldTarget) return

    Array.from(this.fieldTarget.options).forEach((option) => {
      option.selected = false
    })

    this.triggerChange()
  }

  reinitializeAfterTurboRender() {
    this.initializeAttempts = 0
    this.clearInitializeRetryTimer()
    this.clearReinitializeDelayTimer()
    this.destroySelect2()
    this.initializeWithRetry()
    window.requestAnimationFrame(() => this.initializeWithRetry())
    this.reinitializeDelayTimer = window.setTimeout(() => {
      this.initializeWithRetry()
      this.reinitializeDelayTimer = null
    }, 120)
  }

  initializeWithRetry() {
    const initialized = this.initializeSelect2()
    if (initialized) {
      this.clearInitializeRetryTimer()
      return
    }

    if (this.initializeAttempts >= this.maxInitializeAttempts) return

    this.initializeAttempts += 1
    this.clearInitializeRetryTimer()
    this.initializeRetryTimer = window.setTimeout(() => this.initializeWithRetry(), 100)
  }

  initializeSelect2() {
    const $ = window.jQuery || window.$
    if (!this.hasFieldTarget) return false
    if (!($ && typeof $.fn?.select2 === "function")) return false

    const $field = $(this.fieldTarget)
    if ($field.data("select2")) return true

    try {
      const multi = !!this.fieldTarget.multiple
      const allowClear = this.fieldTarget.dataset.allowClear !== "false"
      // Multi: keep open while selecting; single: close after pick.
      const closeOnSelect =
        this.fieldTarget.dataset.closeOnSelect != null
          ? this.fieldTarget.dataset.closeOnSelect !== "false"
          : !multi

      $field.select2({
        width: "100%",
        theme: "default",
        closeOnSelect,
        placeholder: this.fieldTarget.dataset.placeholder || (multi ? "Select…" : "Select…"),
        allowClear,
        dropdownParent: $(document.body),
        dropdownAutoWidth: false
      })
      return !!$field.data("select2")
    } catch (_error) {
      return false
    }
  }

  destroySelect2() {
    const $ = window.jQuery || window.$
    if (!this.hasFieldTarget) return
    if (!($ && typeof $.fn?.select2 === "function")) return

    const $field = $(this.fieldTarget)
    if ($field.data("select2")) $field.select2("destroy")
  }

  clearInitializeRetryTimer() {
    if (!this.initializeRetryTimer) return
    window.clearTimeout(this.initializeRetryTimer)
    this.initializeRetryTimer = null
  }

  clearReinitializeDelayTimer() {
    if (!this.reinitializeDelayTimer) return
    window.clearTimeout(this.reinitializeDelayTimer)
    this.reinitializeDelayTimer = null
  }

  triggerChange() {
    const $ = window.jQuery || window.$
    if ($ && typeof $.fn?.select2 === "function") {
      $(this.fieldTarget).trigger("change")
      return
    }

    this.fieldTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }
}
